const TOCK_SCRIPT_URL = 'https://www.exploretock.com/tock.js';
const SETTINGS_ELEMENT_ID = 'tock-modal-settings';

/** Path segments that are Tock routes rather than a business slug. */
const RESERVED_SEGMENTS = new Set(['experience', 'offering', 'event', 'search', 'gift', 'giftcard']);

/** Query parameters Tock uses to carry an experience id. */
const EXPERIENCE_PARAMS = ['experienceId', 'experience_id', 'experience'];

/**
 * Turns outbound Tock links into in-page modal triggers.
 *
 * Tock's widget script intercepts clicks on any element carrying
 * `data-tock-reserve="true"` and opens the booking flow as an overlay, leaving
 * the plain `href` as the fallback for when the script fails to load. Rather
 * than asking merchandisers to hand-author those attributes on every reserve
 * button, this decorates every `exploretock.com` link on the page and derives
 * the business — and the experience, when the URL deep-links one — from the
 * href itself. Reserve buttons therefore only have to point at Tock.
 *
 * The widget script is injected once at least one Tock link is found, and always
 * after the links are decorated, since Tock scans the DOM as it initialises.
 */

/** @returns {{ suffix: string }} */
function readSettings() {
  const element = document.getElementById(SETTINGS_ELEMENT_ID);
  if (!element?.textContent) return { suffix: '' };

  try {
    const parsed = JSON.parse(element.textContent);
    return { suffix: typeof parsed.suffix === 'string' ? parsed.suffix : '' };
  } catch {
    return { suffix: '' };
  }
}

/**
 * @param {string} hostname
 * @returns {boolean}
 */
function isTockHost(hostname) {
  return hostname === 'exploretock.com' || hostname.endsWith('.exploretock.com');
}

/**
 * @param {URL} url
 * @returns {string} The business slug, or an empty string when the URL has none.
 */
function businessFromUrl(url) {
  const [segment] = url.pathname.split('/').filter(Boolean);
  if (!segment || segment.includes('.') || RESERVED_SEGMENTS.has(segment)) return '';

  return segment;
}

/**
 * @param {URL} url
 * @returns {string} The experience id, or an empty string when the URL has none.
 */
function experienceFromUrl(url) {
  const segments = url.pathname.split('/').filter(Boolean);
  const index = segments.indexOf('experience');
  const fromPath = index === -1 ? '' : (segments[index + 1] ?? '');
  if (/^\d+$/.test(fromPath)) return fromPath;

  for (const param of EXPERIENCE_PARAMS) {
    const value = url.searchParams.get(param) ?? '';
    if (/^\d+$/.test(value)) return value;
  }

  return '';
}

/**
 * @param {ParentNode} root
 * @param {string} suffix - Appended to every business slug, for non-live domains.
 * @returns {Set<string>} Every business linked to from within `root`.
 */
function decorate(root, suffix) {
  /** @type {Set<string>} */
  const businesses = new Set();

  for (const link of root.querySelectorAll('a[href*="exploretock.com"]')) {
    if (!(link instanceof HTMLAnchorElement)) continue;

    /** @type {URL} */
    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch {
      continue;
    }

    if (!isTockHost(url.hostname)) continue;

    const business = businessFromUrl(url);
    if (!business) continue;

    businesses.add(business + suffix);

    // Already decorated, by an earlier pass over the same link.
    if (link.dataset.tockReserve === 'true') continue;

    link.dataset.tockBusiness = business + suffix;

    // A Liquid-set experience id wins: it is the merchandiser being explicit.
    const experience = link.dataset.tockExperience || experienceFromUrl(url);
    if (experience) link.dataset.tockExperience = experience;

    // Set last: Tock treats this as the opt-in marker.
    link.dataset.tockReserve = 'true';
  }

  return businesses;
}

/** Businesses already passed to `tock('init', ...)`. */
const initialised = new Set();

/**
 * Loads the widget script, once, and initialises it for each business.
 *
 * Tock rejects a click whose `data-tock-business` was never initialised, so
 * every business linked from the page needs its own `init` — a page offering
 * experiences at more than one winery would otherwise only book the first.
 *
 * @param {Iterable<string>} businesses
 */
function loadTockScript(businesses) {
  /* eslint-disable */
  // Tock's own loader, verbatim apart from formatting: it queues calls until the
  // real script takes over, so the `init` calls below are safe to make now.
  !(function (t, o, c, k) {
    if (!t.tock) {
      var e = (t.tock = function () {
        e.callMethod ? e.callMethod.apply(e, arguments) : e.queue.push(arguments);
      });
      t._tock || (t._tock = e);
      e.push = e;
      e.loaded = !0;
      e.version = '1.0';
      e.queue = [];
      var f = o.createElement(c);
      f.async = !0;
      f.src = k;
      var g = o.getElementsByTagName(c)[0];
      g.parentNode.insertBefore(f, g);
    }
  })(window, document, 'script', TOCK_SCRIPT_URL);
  /* eslint-enable */

  for (const business of businesses) {
    if (initialised.has(business)) continue;

    initialised.add(business);
    window.tock('init', business);
  }
}

/**
 * Keeps the page from jumping to the top while the modal is open.
 *
 * Tock locks scrolling with `position: fixed` on the body without offsetting it,
 * so the viewport snaps to the top on open and stays there on close. Recording
 * the scroll position on click and mirroring it into `top` preserves it.
 */
let scrollPositionPreserved = false;

function preserveScrollPosition() {
  if (scrollPositionPreserved) return;
  scrollPositionPreserved = true;

  let scrollY = 0;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-tock-reserve]')) scrollY = window.scrollY;
    },
    true
  );

  const body = document.body;

  new MutationObserver(() => {
    if (body.style.position === 'fixed') {
      if (!body.style.top || body.style.top === '0px') body.style.top = `-${scrollY}px`;
    } else if (body.style.top) {
      body.style.top = '';
    }
  }).observe(body, { attributes: true, attributeFilter: ['style'] });
}

const { suffix } = readSettings();

/** @param {ParentNode} root */
function enableWithin(root) {
  const businesses = decorate(root, suffix);
  if (businesses.size === 0) return;

  preserveScrollPosition();
  loadTockScript(businesses);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enableWithin(document), { once: true });
} else {
  enableWithin(document);
}

// Sections re-rendered by the theme editor arrive with undecorated links.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) enableWithin(event.target);
});
